import { Check, Hand, Trash2 } from "lucide-react";
import type { FC, ReactNode } from "react";

import { workflowDisplayText } from "@/features/caseIde/workflows/catalog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { relativeTime } from "@/features/agents/lib/time";
import { cn } from "@/lib/utils";

import type { CaseAgentThread } from "../api/caseAgentApi";
import { initials, plainMentions, type TeamMember } from "../mentions";

/** Who is who in a thread row: the firm's members and the signed-in uid. */
export type ThreadPeople = {
  members: readonly TeamMember[];
  meUid: string | null;
};

const MAX_JOINED = 3;

export const memberOf = (
  people: ThreadPeople | undefined,
  uid: string | null | undefined
): TeamMember | null =>
  uid ? people?.members.find((m) => m.uid === uid) ?? null : null;

/** "You" for the signed-in person, else the member's name. */
export const personLabel = (
  people: ThreadPeople | undefined,
  uid: string | null | undefined
): string | null => {
  if (!uid) return null;
  if (people?.meUid && uid === people.meUid) return "You";
  return memberOf(people, uid)?.label ?? null;
};

/** Teammates who posted after the starter. */
export const joinedOf = (
  thread: CaseAgentThread,
  people: ThreadPeople | undefined
): TeamMember[] =>
  (thread.participants ?? [])
    .filter((uid) => uid !== thread.created_by)
    .map((uid) => memberOf(people, uid))
    .filter((m): m is TeamMember => m !== null);

export const PersonAvatar: FC<{
  member: Pick<TeamMember, "label" | "avatarUrl">;
  className?: string;
}> = ({ member, className }) => (
  <Avatar size="2xs" className={className} title={member.label}>
    {member.avatarUrl ? <AvatarImage src={member.avatarUrl} alt="" /> : null}
    <AvatarFallback>{initials(member.label)}</AvatarFallback>
  </Avatar>
);

/**
 * Overlapping avatars of the teammates who joined, at most three. `cutout` is
 * the ring color of the surface behind them.
 */
export const JoinedStack: FC<{
  joined: TeamMember[];
  cutout?: "ring-popover" | "ring-surface";
}> = ({ joined, cutout = "ring-popover" }) =>
  joined.length ? (
    <span className="flex shrink-0 items-center -space-x-1">
      {joined.slice(0, MAX_JOINED).map((m) => (
        <PersonAvatar
          key={m.uid}
          member={m}
          className={cn("ring-cutout", cutout)}
        />
      ))}
    </span>
  ) : null;

/** `text` with the first case-insensitive match of `query` marked. */
export const Highlight: FC<{ text: string; query?: string }> = ({
  text,
  query
}) => {
  const q = (query ?? "").trim().toLowerCase();
  const at = q ? text.toLowerCase().indexOf(q) : -1;
  if (at < 0) return text;
  return (
    <>
      {text.slice(0, at)}
      <mark className="rounded-xs bg-accent-soft text-fg">
        {text.slice(at, at + q.length)}
      </mark>
      {text.slice(at + q.length)}
    </>
  );
};

type RowProps = {
  thread: CaseAgentThread;
  active: boolean;
  unread?: number;
  /** Search text to mark in the title and preview. */
  query?: string;
  /** Resolves the starter and joined teammates; without it the row shows the age only. */
  people?: ThreadPeople;
  /** Trailing control (the delete button). */
  trailing?: ReactNode;
};

/**
 * One conversation, three lines: the title with approval and unread marks; the
 * last message; who started it and when, with the teammates who joined.
 */
export const ThreadRow: FC<RowProps> = ({
  thread: t,
  active,
  unread = 0,
  query,
  people,
  trailing
}) => {
  const starter = memberOf(people, t.created_by);
  const starterLabel = personLabel(people, t.created_by);
  const joined = joinedOf(t, people);
  const age = relativeTime(t.updated_at ?? t.created_at);
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      <div className="flex min-w-0 items-center gap-1.5">
        <span className="truncate text-base font-medium text-fg">
          <Highlight
            text={plainMentions(workflowDisplayText(t.title))}
            query={query}
          />
        </span>
        {t.pending_approval ? (
          <Hand
            className="size-3 shrink-0 text-warn"
            aria-label="Awaiting approval"
          />
        ) : null}
        {unread > 0 ? (
          <Badge
            variant="default"
            className="h-4 min-w-4 px-1 text-xs tabular-nums"
            data-testid="thread-unread-badge"
            aria-label={`${unread} unread mentions`}
          >
            {unread}
          </Badge>
        ) : null}
        <span className="flex-1" />
        {trailing}
        <Check
          className={cn(
            "size-3 shrink-0 text-fg-3",
            active ? "opacity-100" : "opacity-0"
          )}
        />
      </div>
      {t.last_message_preview ? (
        <div className="truncate text-sm text-fg-3">
          <Highlight
            text={plainMentions(workflowDisplayText(t.last_message_preview))}
            query={query}
          />
        </div>
      ) : null}
      <div className="flex min-w-0 items-center gap-1.5 pt-px text-sm">
        {starter && starterLabel ? (
          <>
            <PersonAvatar member={starter} />
            <span className="truncate text-fg-2">{starterLabel}</span>
          </>
        ) : starterLabel ? (
          <span className="truncate text-fg-2">{starterLabel}</span>
        ) : null}
        {age ? (
          <span className="shrink-0 text-fg-4 tabular-nums">
            {starterLabel ? `· ${age}` : age}
          </span>
        ) : null}
        <span className="flex-1" />
        <JoinedStack joined={joined} />
      </div>
    </div>
  );
};

type Props = Omit<RowProps, "trailing"> & {
  onSelect: () => void;
  /** Shown as a trash icon on hover; omit to hide it. */
  onDelete?: () => void;
};

export const DeleteThreadButton: FC<{ onDelete: () => void }> = ({
  onDelete
}) => (
  <button
    type="button"
    aria-label="Delete conversation"
    className="shrink-0 rounded-xs p-0.5 text-fg-3 opacity-0 hover:text-danger group-hover:opacity-100"
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      onDelete();
    }}
  >
    <Trash2 className="size-3" />
  </button>
);

/** One conversation in a dropdown thread menu (the Case IDE's history). */
export const ThreadMenuItem: FC<Props> = ({ onSelect, onDelete, ...row }) => (
  <DropdownMenuItem onSelect={onSelect} className="group flex items-start">
    <ThreadRow
      {...row}
      trailing={onDelete ? <DeleteThreadButton onDelete={onDelete} /> : null}
    />
  </DropdownMenuItem>
);
